import { AssetInfoCard } from "components/AssetInfoCard";
import { MsgExecuteContract } from "@goblinhunt/cosmes/client";
import { PoolResponse } from "models/staking/PoolResponse";
import { BalanceResponse } from "models/token/BalanceResponse";
import { useAsset } from "provider/AssetProvider"
import { useWallet } from "provider/WalletProvider";
import { SetStateAction, useCallback, useEffect, useState } from "react"
import { CLASSIC_CHAIN_ID } from "utils/Constants";
import { fromDenom, numberPendek, toDenom } from "utils/Helpers"
import { showSimpleModal } from "utils/Modals";
import { querySmartContract } from "utils/RpcHelpers";
import { showLoadingToast, showTxToast } from "utils/Toasts";

export function ManagePoolPage() {
    const { post, address: sender } = useWallet()
    const { stakingAddress, tokenInfo, tokenAddress } = useAsset()
    const [poolInfo, setPoolInfo] = useState<PoolResponse>();
    const [balanceInfo, setBalanceInfo] = useState<BalanceResponse>();
    const [increasePool, setIncreasePool] = useState('0');
    const [decreasePool, setDecreasePool] = useState('0');

    useEffect(() => {
        if (sender && stakingAddress != undefined && tokenAddress != undefined) {
            loadTotalPool()
            loadBalance()
        }
    }, [stakingAddress, tokenAddress, sender])

    const loadTotalPool = async () => {
        try {
            const res: PoolResponse = await querySmartContract(stakingAddress!, {
                "pool": {}
            })
            setPoolInfo(res)
        } catch (e) { }
    }

    const loadBalance = async () => {
        try {
            const res: BalanceResponse = await querySmartContract(tokenAddress!, {
                "balance": {
                    "address": sender
                }
            })
            setBalanceInfo(res)
        } catch (e) { }
    }

    const onIncreasePool = async () => {

        let increaseMsg = {
            increase_pool: {
            }
        }

        let sendTokenMsg = {
            send: {
                contract: stakingAddress, // contract tujuan execute setelah kirim token (RVN token)
                msg: Buffer.from(JSON.stringify(increaseMsg)).toString("base64"),
                amount: toDenom(Number(increasePool), tokenInfo?.decimals ?? 6).toString()
            }
        }

        let executeMsg = new MsgExecuteContract({
            sender: sender!,
            contract: tokenAddress!,
            msg: sendTokenMsg,
            funds: []
        })
        const msgs = [executeMsg]

        let result = await showLoadingToast("Signing transaction", () => post({
            msgs,
        }))

        await showTxToast(result)
        loadBalance()
        loadTotalPool()
    }

    const onDecreasePool = async () => {

        let decreaseMsg = {
            decrease_pool: {
                amount: toDenom(Number(decreasePool), tokenInfo?.decimals ?? 6).toString()
            }
        }

        let executeMsg = new MsgExecuteContract({
            sender: sender!,
            contract: stakingAddress!,
            msg: decreaseMsg,
            funds: []
        })
        const msgs = [executeMsg]

        let result = await showLoadingToast("Signing transaction", () => post({
            msgs,
        }))

        await showTxToast(result)
        loadBalance()
        loadTotalPool()
    }

    const increaseValid = () => {
        if (!sender) {
            return "Please connect your wallet first"
        }

        if (!tokenInfo) {
            return "Cannot read token info, please make sure the token address is valid"
        }

        const increaseNum = Number(increasePool)
        if (isNaN(increaseNum) || increaseNum <= 0) return "Increase amount must be more than 0, use . (period) as decimal separator"

        return undefined;
    }

    const decreaseValid = () => {
        if (!sender) {
            return "Please connect your wallet first"
        }

        if (!tokenInfo) {
            return "Cannot read token info, please make sure the token address is valid"
        }

        const increaseNum = Number(decreasePool)
        if (isNaN(increaseNum) || increaseNum <= 0) return "Decrease amount must be more than 0, use . (period) as decimal separator"

        return undefined;
    }

    return <div className="container-fluid container-p-y">
        <div className="row">
            <div className="col-lg-5">
                <div className="row">
                    <div className="col-lg-6 col-md-6 col-6 mb-4">
                        <AssetInfoCard
                            label="Total Pool"
                            icon="dollar"
                            color="primary"
                            tokenInfo={tokenInfo}
                            amount={fromDenom(Number(poolInfo?.pool.total_pool ?? "0"), tokenInfo?.decimals ?? 6 ?? 6)} />
                    </div>
                    <div className="col-lg-6 col-md-6 col-6 mb-4">
                        <AssetInfoCard
                            label="Balance"
                            icon="wallet"
                            color="info"
                            tokenInfo={tokenInfo}
                            amount={fromDenom(Number(balanceInfo?.balance ?? "0"), tokenInfo?.decimals ?? 6 ?? 6)} />
                    </div>
                </div>
            </div>
            <div className="col-lg-7">
                <div className="nav-align-top mb-4">
                    <ul className="nav nav-tabs nav-fill" role="tablist">
                        <li className="nav-item">
                            <button type="button" className="nav-link active" role="tab" data-bs-toggle="tab" data-bs-target="#navs-justified-profile" aria-controls="navs-justified-profile" aria-selected="false">
                                <i className='bx bx-message-square-add'></i> Increase
                            </button>
                        </li>
                        <li className="nav-item">
                            <button type="button" className="nav-link" role="tab" data-bs-toggle="tab" data-bs-target="#navs-justified-messages" aria-controls="navs-justified-messages" aria-selected="false">
                                <i className='bx bx-message-square-minus'></i> Decrease
                            </button>
                        </li>
                    </ul>
                    <div className="tab-content">
                        <div className="tab-pane fade show active" id="navs-justified-profile" role="tabpanel">
                            <div className="mb-3">
                                <label htmlFor="staking-apr" className="form-label">Increase Amount</label>
                                <div className="input-group">
                                    <input
                                        onChange={({ target }) => setIncreasePool(target.value)}
                                        value={increasePool}
                                        type="number"
                                        className="form-control"
                                        id="increase-pool"
                                        placeholder="Enter amount"
                                    />
                                    <span className="input-group-text">{tokenInfo?.symbol}</span>
                                </div>
                            </div>
                            {
                                increaseValid() && <div className="alert alert-danger" role="alert">{increaseValid()}</div>
                            }
                            <button
                                disabled={increaseValid() != undefined}
                                onClick={onIncreasePool}
                                className="btn btn-primary deactivate-account">Increase</button>
                        </div>
                        <div className="tab-pane fade" id="navs-justified-messages" role="tabpanel">
                            <div className="mb-3">
                                <label htmlFor="staking-apr" className="form-label">Decrease Amount</label>
                                <div className="input-group">
                                    <input
                                        onChange={({ target }) => setDecreasePool(target.value)}
                                        value={decreasePool}
                                        type="number"
                                        className="form-control"
                                        id="increase-pool"
                                        placeholder="Enter amount"
                                    />
                                    <span className="input-group-text">{tokenInfo?.symbol}</span>
                                </div>
                            </div>
                            {
                                decreaseValid() && <div className="alert alert-danger" role="alert">{decreaseValid()}</div>
                            }
                            <button
                                disabled={decreaseValid() != undefined}
                                onClick={onDecreasePool}
                                className="btn btn-primary deactivate-account">Decrease</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

}